// Central operacional e financeira do evento.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Banknote, Bus, CalendarDays, CircleDollarSign, Loader2, MapPin, Package, Percent, Receipt, Ticket, TrendingUp, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { C, KpiCard, KpiGrid, ModuleHeader, PageShell, StatusBadge } from '@/design-system/dna';
import { brl, fmtDate, STATUS_LABEL, TIPO_LABEL } from './shared';
import { VisaoGeral } from './tabs/VisaoGeral';
import { TurmasAlunos } from './tabs/TurmasAlunos';
import { Recebimentos } from './tabs/Recebimentos';
import { Custos } from './tabs/Custos';
import { Autorizacoes } from './tabs/Autorizacoes';
import { Financeiro } from './tabs/Financeiro';
import { Extras } from './tabs/Extras';

const TABS = [['geral', 'Visão Geral'], ['turmas', 'Turmas e Alunos'], ['recebimentos', 'Recebimentos'], ['extras', 'Extras'], ['custos', 'Custos'], ['autorizacoes', 'Autorizações'], ['financeiro', 'Financeiro']];

function Kpis({ d }) {
  const cards = [
    ['receita-evento', 'Receita Evento', brl(d.receita_evento), `${d.participantes} participante(s)${d.capacidade ? ` · cap. ${d.capacidade}` : ''}`, CircleDollarSign, C.green],
    ['receita-extras', 'Receita Extras', brl(d.receita_extras), `convites ${brl(d.receita_convites)} · estac. ${brl(d.receita_estacionamento)} · outros ${brl(d.receita_outros)}`, Ticket, C.purple],
    ['custo-evento', 'Custos do Evento', brl(d.custo_evento), 'por criança + fixos + avulsos', Receipt, C.orange],
    ['custo-extras', 'Custos dos Extras', brl(d.custo_extras), `lucro dos extras ${brl(d.lucro_extras)}`, Package, C.ink],
    ['taxas', 'Taxas', brl(d.taxas), `${d.pagamentos} pagamento(s)`, Percent, C.amber],
    ['liquida', 'Receita Líquida', brl(d.receita_liquida), `a receber ${brl(d.a_receber)}`, Wallet, C.leaf],
    ['lucro', 'Lucro Real', brl(d.lucro), 'receitas − taxas − custos do evento − custos dos extras', TrendingUp, d.lucro >= 0 ? C.green : C.magenta],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7" data-testid="evento-kpis">
      {cards.map(([id, label, value, hint, Icon, color], i) => <KpiCard key={id} id={`evento-${id}`} label={label} value={value} hint={hint} icon={Icon} color={color} index={i} />)}
    </div>
  );
}

function BoardingButton({ eventId, name }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/eventos/${eventId}/lista-embarque.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `lista_embarque_${(name || 'evento').toLowerCase().replace(/\s+/g, '_')}.pdf`; a.click();
      window.open(url, '_blank');
    } catch { toast.error('Falha ao gerar a lista de embarque.'); } finally { setBusy(false); }
  };
  return <Button size="sm" onClick={go} disabled={busy} className="rounded-full bg-white/10 text-white ring-1 ring-white/20 hover:bg-white/20" data-testid="evento-lista-embarque">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bus className="h-4 w-4" />} Lista de Embarque</Button>;
}

export default function EventoCentral() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('geral');
  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/eventos/${id}`); setData(d); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao carregar o evento.'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <PageShell><p className="flex items-center gap-2 py-16 text-sm" style={{ color: C.muted }}><Loader2 className="h-4 w-4 animate-spin" /> Carregando evento…</p></PageShell>;
  const ev = data.event;
  const ctx = { data, setData, reload: load, eventId: id };
  return (
    <PageShell testid="evento-central">
      <Link to="/v2/festa-junina/eventos" className="inline-flex w-fit items-center gap-1.5 text-[13px] font-semibold" style={{ color: C.purple }} data-testid="evento-voltar"><ArrowLeft className="h-4 w-4" /> Eventos</Link>
      <ModuleHeader kicker={`Evento · ${TIPO_LABEL[ev.tipo] || ev.tipo}`} title={ev.nome} icon={Banknote} testid="evento-header"
        subtitle={<span className="flex flex-wrap items-center gap-x-4 gap-y-1"><span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{ev.date_iso ? fmtDate(ev.date_iso) : 'sem data'} {ev.horario_inicio || ''}</span>{ev.local && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ev.local}</span>}<span>{brl(ev.valor_aluno)} por aluno</span></span>}
        actions={<><BoardingButton eventId={id} name={ev.nome} /><StatusBadge tone={ev.status === 'cancelado' ? 'danger' : ev.status === 'realizado' ? 'success' : 'info'} testid="evento-status">{STATUS_LABEL[ev.status] || ev.status}</StatusBadge></>} />
      <Kpis d={data.dashboard} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-2xl bg-white p-1.5 border" style={{ borderColor: C.line }} data-testid="evento-tabs">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-xl px-4 py-2 data-[state=active]:bg-[#2A1240] data-[state=active]:text-white" data-testid={`evento-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="geral" className="mt-4"><VisaoGeral {...ctx} /></TabsContent>
        <TabsContent value="turmas" className="mt-4"><TurmasAlunos {...ctx} /></TabsContent>
        <TabsContent value="recebimentos" className="mt-4"><Recebimentos {...ctx} /></TabsContent>
        <TabsContent value="extras" className="mt-4">{tab === 'extras' && <Extras {...ctx} />}</TabsContent>
        <TabsContent value="custos" className="mt-4"><Custos {...ctx} /></TabsContent>
        <TabsContent value="autorizacoes" className="mt-4"><Autorizacoes {...ctx} /></TabsContent>
        <TabsContent value="financeiro" className="mt-4"><Financeiro {...ctx} active={tab === 'financeiro'} /></TabsContent>
      </Tabs>
    </PageShell>
  );
}
